import { useState, useCallback, useEffect } from 'react'
import {
    listGroups,
    createGroup,
    updateGroup,
    deleteGroupApi,
    addAccountToGroup,
    removeAccountFromGroup,
    importGroupsFromAccount,
    TelegramGroup,
} from '../../../api/telegramApi'

interface UseTelegramGroupsReturn {
    // Groups data
    groups: TelegramGroup[]
    loading: boolean

    // Alerts
    message: string | null
    error: string | null
    setMessage: (message: string | null) => void
    setError: (error: string | null) => void
    resetAlerts: () => void

    // Actions
    fetchGroups: () => Promise<void>
    handleCreateGroup: (data: { peerId: string; joinLink?: string; originalName?: string; brokerCompanyName?: string }) => Promise<void>
    handleUpdateGroup: (
        peerId: string,
        data: { joinLink?: string | null; status?: string; originalName?: string | null; brokerCompanyName?: string | null }
    ) => Promise<void>
    handleDeleteGroup: (peerId: string) => Promise<void>
    handleAddAccountToGroup: (peerId: string, phone: string) => Promise<void>
    handleRemoveAccountFromGroup: (peerId: string, phone: string) => Promise<void>
    handleImportFromAccount: (phone: string) => Promise<void>
}

export function useTelegramGroups(): UseTelegramGroupsReturn {
    const [groups, setGroups] = useState<TelegramGroup[]>([])
    const [loading, setLoading] = useState(false)

    // Alerts
    const [message, setMessage] = useState<string | null>(null)
    const [error, setError] = useState<string | null>(null)

    const resetAlerts = useCallback(() => {
        setMessage(null)
        setError(null)
    }, [])

    const fetchGroups = useCallback(async () => {
        setLoading(true)
        try {
            const rows = await listGroups()
            setGroups(rows)
        } catch (e) {
            setError((e as Error)?.message || 'Failed to load groups')
        } finally {
            setLoading(false)
        }
    }, [])

    const handleCreateGroup = useCallback(
        async (data: { peerId: string; joinLink?: string; originalName?: string; brokerCompanyName?: string }) => {
            try {
                await createGroup(data)
                setMessage('Group created successfully.')
                await fetchGroups()
            } catch (e) {
                setError((e as Error)?.message || 'Failed to create group')
            }
        },
        [fetchGroups]
    )

    const handleUpdateGroup = useCallback(
        async (peerId: string, data: { joinLink?: string | null; status?: string; originalName?: string | null; brokerCompanyName?: string | null }) => {
            try {
                await updateGroup(peerId, data)
                setMessage('Group updated successfully.')
                await fetchGroups()
            } catch (e) {
                setError((e as Error)?.message || 'Failed to update group')
            }
        },
        [fetchGroups]
    )

    const handleDeleteGroup = useCallback(
        async (peerId: string) => {
            try {
                await deleteGroupApi(peerId)
                setMessage('Group deleted successfully.')
                await fetchGroups()
            } catch (e) {
                setError((e as Error)?.message || 'Failed to delete group')
            }
        },
        [fetchGroups]
    )

    const handleAddAccountToGroup = useCallback(
        async (peerId: string, phone: string) => {
            try {
                await addAccountToGroup(peerId, phone)
                setMessage('Account added to group successfully.')
                await fetchGroups()
            } catch (e) {
                setError((e as Error)?.message || 'Failed to add account to group')
            }
        },
        [fetchGroups]
    )

    const handleRemoveAccountFromGroup = useCallback(
        async (peerId: string, phone: string) => {
            try {
                await removeAccountFromGroup(peerId, phone)
                setMessage('Account removed from group successfully.')
                await fetchGroups()
            } catch (e) {
                setError((e as Error)?.message || 'Failed to remove account from group')
            }
        },
        [fetchGroups]
    )

    const handleImportFromAccount = useCallback(
        async (phone: string) => {
            try {
                const result = await importGroupsFromAccount(phone)
                setMessage(`Imported ${result.imported} groups from account.`)
                await fetchGroups()
            } catch (e) {
                setError((e as Error)?.message || 'Failed to import groups from account')
            }
        },
        [fetchGroups]
    )

    useEffect(() => {
        void fetchGroups()
    }, [fetchGroups])

    return {
        groups,
        loading,
        message,
        error,
        setMessage,
        setError,
        resetAlerts,
        fetchGroups,
        handleCreateGroup,
        handleUpdateGroup,
        handleDeleteGroup,
        handleAddAccountToGroup,
        handleRemoveAccountFromGroup,
        handleImportFromAccount,
    }
}
